import type { GetResult } from "@/api/types";

import type { GuestPosition } from "./requestGuestPosition";

type LocationCoordinate = GetResult<"/platform-location/coordinates">["locations"][number];

const EARTH_RADIUS_METERS = 6_371_000;
const MAX_SUGGESTION_DISTANCE_METERS = 200;
const toRadians = (degrees: number) => (degrees * Math.PI) / 180;
export const isCoordinate = (latitude: number | null, longitude: number | null) =>
  latitude !== null &&
  longitude !== null &&
  Number.isFinite(latitude) &&
  Number.isFinite(longitude) &&
  Math.abs(latitude) <= 90 &&
  Math.abs(longitude) <= 180;

/** Great-circle (haversine) distance from `position` to the given coordinate. */
export const distanceMeters = (
  position: Pick<GuestPosition, "latitude" | "longitude">,
  latitude: number,
  longitude: number,
) => {
  const latitudeDifference = toRadians(latitude - position.latitude);
  const longitudeDifference = toRadians(longitude - position.longitude);
  const haversine =
    Math.sin(latitudeDifference / 2) ** 2 +
    Math.cos(toRadians(position.latitude)) *
      Math.cos(toRadians(latitude)) *
      Math.sin(longitudeDifference / 2) ** 2;
  // Floating-point rounding can put antipodal points just outside the square root's domain.
  const boundedHaversine = Math.min(1, Math.max(0, haversine));
  return (
    2 *
    EARTH_RADIUS_METERS *
    Math.atan2(Math.sqrt(boundedHaversine), Math.sqrt(1 - boundedHaversine))
  );
};

/**
 * A branch is suggested only when its worst-case distance is at most 200 meters and less than
 * every competitor's best-case distance within the browser's reported accuracy radius. This
 * identifies proximity; it does not establish that the visitor is physically inside a branch.
 */
export const findUnambiguousNearestLocation = (
  locations: LocationCoordinate[],
  position: GuestPosition,
): string | null => {
  if (
    locations.length < 2 ||
    !isCoordinate(position.latitude, position.longitude) ||
    !Number.isFinite(position.accuracy) ||
    position.accuracy <= 0
  ) {
    return null;
  }

  const distances: { locationId: string; meters: number }[] = [];
  for (const location of locations) {
    // Unknown branches must remain candidates: excluding one could falsely identify the winner.
    if (
      location.latitude === null ||
      location.longitude === null ||
      !isCoordinate(location.latitude, location.longitude)
    ) {
      return null;
    }
    distances.push({
      locationId: location.locationId,
      meters: distanceMeters(position, location.latitude, location.longitude),
    });
  }

  distances.sort((left, right) => left.meters - right.meters);
  const [nearest, runnerUp] = distances;
  const maximumNearestDistance = nearest.meters + position.accuracy;
  return maximumNearestDistance <= MAX_SUGGESTION_DISTANCE_METERS &&
    maximumNearestDistance < runnerUp.meters - position.accuracy
    ? nearest.locationId
    : null;
};
